import React, {ReactNode} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import Settings from 'Settings'
import {toggle} from 'opticks/lib/optimizely'
import styled from '@emotion/styled'
import css from '@emotion/css'

import {updateUserMetadata} from '@daedalus/core/src/user/services'
import Header from '@daedalus/atlas/src/components/shared/general/Header'
import HeaderBSide from '@daedalus/atlas/src/components/shared/general/Header/BSide'
import CompactHeader from '@daedalus/atlas/src/components/shared/general/CompactHeader'
import {useDeviceLayout} from '@daedalus/atlas/src/context/deviceLayout'
import {
  splitPopularAndOtherCurrencies,
  getEnabledCurrencies
} from '@daedalus/core/src/localization/business/currency'
import {TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY} from '@daedalus/core/src/auth/modules/actions'
import {selectIsAuthenticated} from '@daedalus/core/src/auth/modules/selectors'
import {
  useAuthEnabled,
  useCognitoUser,
  useIsMemberPlus
} from '@daedalus/core/src/auth/modules/useCognito'

import {PreferencesWidgetWithStore} from 'components/PreferencesWidgetWithStore'
import UserWidgetWithStore from 'components/UserWidgetWithStore'
import PreferencesOverlay from 'components/PreferencesOverlay'
import useParams from 'components/data/URLParams/useParams'
import {changeLanguage} from 'modules/localization/actions/changeLanguage'
import {changeCurrency} from 'modules/localization/actions/changeCurrency'
import {changePreferencesVisibility} from 'modules/overlay/actions'
import {trackEventNew} from 'modules/analytics/actions'
import {
  EventName,
  EventCategory,
  EventAction
} from 'modules/analytics/actions/trackEventNew'
import {getPageSpace} from 'modules/analytics/trackEventNewHelpers'
import availableLanguages from '@daedalus/core/src/localization/services/locale'
import brand from 'configs/brand'
import MembershipOverlay from '../MembershipOverlay'

type PropsType = {
  children?: ReactNode
  currentCurrency: string
  currentLanguage: string
  disableLogoClick?: boolean
  disablePreferences?: boolean
  onActionButtonClick: () => void
  isUserWidgetVisible?: boolean
  hasLanguageSelector?: boolean
  hasCurrencySelector?: boolean
  hasShadow?: boolean
}

const BSideWrapper = styled('div')(
  ({theme}) =>
    css`
      border-bottom: solid 1px ${theme.colors.headerBorder};
    `
)

const HeaderWithStore = ({
  children,
  disableLogoClick,
  disablePreferences,
  onActionButtonClick,
  currentCurrency,
  currentLanguage,
  isUserWidgetVisible,
  hasLanguageSelector,
  hasCurrencySelector,
  hasShadow
}: PropsType) => {
  const dispatch = useDispatch()
  const authEnabled = useAuthEnabled()
  const user = useCognitoUser()
  const isAuthenticated = useSelector(selectIsAuthenticated)
  const isMemberPlus = useIsMemberPlus()

  const urlParams = useParams()
  const space = getPageSpace(window.location.pathname)

  const onChangeLanguage = async language => {
    if (isAuthenticated) await updateUserMetadata({language})
    dispatch(
      trackEventNew({
        name: EventName.LANGUAGE_CHANGE,
        category: EventCategory.USER_ACTION,
        space,
        action: EventAction.CLICK,
        value: language,
        urlParams
      })
    )
    setTimeout(() => {
      dispatch(changeLanguage(language))
    }, TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY)
  }

  const onChangeCurrency = value => {
    dispatch(
      trackEventNew({
        name: EventName.CURRENCY_CHANGE,
        category: EventCategory.USER_ACTION,
        space,
        action: EventAction.CLICK,
        value,
        urlParams
      })
    )

    setTimeout(() => {
      dispatch(changeCurrency(value))
    }, TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY)
  }

  const handleShowPreferences = () => {
    dispatch(changePreferencesVisibility(true)())
  }

  const enabledCurrencies = getEnabledCurrencies(
    Settings.get('REACT_APP_ENABLED_CURRENCIES')
  )

  const {currenciesList, popularCurrenciesList} =
    splitPopularAndOtherCurrencies(enabledCurrencies)

  const {isDesktopXs, isMobile} = useDeviceLayout()
  const showBackButton = isDesktopXs

  return isMobile ? (
    <div>
      <CompactHeader
        showBackButton
        disableLogoClick={disableLogoClick}
        showCenterLogo={!children}
        showSettings={!disablePreferences}
        brand={brand}
        shouldShowUserAvatar={authEnabled}
        user={user}
        hasShadow={hasShadow}
        isMemberPlus={isMemberPlus}
        onShowPreferences={handleShowPreferences}
        onBackButtonClick={onActionButtonClick}
      >
        {children}
      </CompactHeader>
      {!disablePreferences && (
        <>
          <PreferencesOverlay
            currentCurrency={currentCurrency}
            currentLanguage={currentLanguage}
            hasLanguageSelector={hasLanguageSelector}
            hasCurrencySelector={hasCurrencySelector}
          />
          <MembershipOverlay />
        </>
      )}
    </div>
  ) : (
    toggle(
      'dq82hd-desktop-map',
      <Header
        brand={brand}
        disableLogoClick={disableLogoClick}
        disablePreferences={disablePreferences}
        showBackButton={showBackButton}
        availableLanguages={availableLanguages}
        currentLanguage={currentLanguage}
        currenciesList={currenciesList}
        popularCurrenciesList={popularCurrenciesList}
        currentCurrency={currentCurrency}
        UserWidget={isUserWidgetVisible && UserWidgetWithStore}
        hasLanguageSelector={hasLanguageSelector}
        hasCurrencySelector={hasCurrencySelector}
        onActionButtonClick={onActionButtonClick}
        onChangeLanguage={onChangeLanguage}
        onChangeCurrency={onChangeCurrency}
      />,
      <BSideWrapper>
        <HeaderBSide
          brand={brand}
          disableLogoClick={disableLogoClick}
          disablePreferences={disablePreferences}
          hasCurrencySelector={hasCurrencySelector}
          showBackButton={showBackButton}
          PreferencesWidget={PreferencesWidgetWithStore}
          UserWidget={isUserWidgetVisible && UserWidgetWithStore}
          onActionButtonClick={onActionButtonClick}
        />
      </BSideWrapper>
    )
  )
}

HeaderWithStore.defaultProps = {
  isUserWidgetVisible: true
}

export default HeaderWithStore
